/**
 * BuyCreditsModal — bundle picker + Stripe checkout launcher.
 *
 * Push #82.8 Phase 2 (2026-06-19) — Anthropic-style two-step flow per
 * Ean's directive. Replaces the Push #82 single-click pattern (tile
 * click → immediate checkout) with: tile click → SELECTS the bundle →
 * "Total due" summary appears → "Pay $X.XX now" footer CTA fires the
 * checkout. Matches the reference UX from Anthropic's "Need more usage?"
 * dialog: discrete tile picker on top, total + Pay button on the bottom.
 *
 * Three reasons this beats the prior single-click pattern:
 *   - Confirmation: users see the bundle they're about to charge for
 *     before money changes hands. Mis-clicks on $1,000 don't go straight
 *     to Stripe.
 *   - Visual hierarchy: the price + CTA in the footer is the dominant
 *     element. The tiles read as discrete options, not action buttons.
 *   - Consistency: matches every other commerce flow ship (RequestModal,
 *     ConfirmDialog) — pick, confirm in footer, then commit.
 *
 * Wires from three surfaces:
 *   - /admin/billing Overview tab + Buy tab (post Push #82.8 rebuild)
 *   - Profile wallet card (Buy more credits link)
 *   - ChatApprovalBanner owner-path (Top up credits CTA)
 *
 * UX behavior is identical pre- and post-Stripe. The user picks a bundle,
 * clicks Pay $X now, the call either:
 *   (a) STRIPE_ENABLED=true → redirects to Stripe Checkout → on
 *       checkout.session.completed webhook fires → billing__token_grant
 *       row inserted → user lands on /admin/billing?purchase=success
 *   (b) STRIPE_ENABLED=false → endpoint returns 503 "Payments coming soon"
 *       → modal flips to a "Payments coming soon" state with a hint that
 *       admins/owners can use the Buy tab to grant test credits.
 *
 * Bundle structure (Ean lock 2026-06-15) — 6 tiers with progressive bonus
 * curve. 1 credit = $0.02 baseline; bigger bundles get a bonus multiplier.
 *
 * Why a modal not a page: the user reached "Buy credits" from a contextual
 * surface (composer chip, banner CTA, billing tile). Sending them off-page
 * loses the context.
 */

import { useEffect, useRef, useState } from 'react';

import {
  buyCheckoutTokenPack,
  fetchBillingProducts,
  fetchSavedCard,
  purchaseSavedCard,
  ApiError,
  type BillingProduct,
  type SavedCardResponse,
} from '../lib/api.ts';
import { formatDollars } from '../lib/credits-format.ts';
import { IconAlert, IconCheck, IconCoin } from './Icon.tsx';
import { OlyticThinkingIndicator } from '../pages/chat/OlyticThinkingIndicator.tsx';

// Brand-token → display-name map. Duplicated from SavedCardSection to
// keep the caption self-contained; when Ean lands the fifth adopter,
// promote to a shared helper in components/billing/. Fallback: raw
// brand string (Stripe's brands are already TitleCase-ish).
const CARD_BRAND_DISPLAY: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

function formatCardBrand(brand: string | undefined): string {
  if (brand === undefined || brand === '') return 'Card';
  const lower = brand.toLowerCase();
  return CARD_BRAND_DISPLAY[lower] ?? brand;
}

// Bundle shape used by the modal — derived from BillingProduct rows the
// server returns. Post D-189 pricebook consolidation, each billing__product
// row IS a buyable tier (no nested tiers[] indirection).
interface Bundle {
  /** billing__product.id; sent to /api/billing/checkout/token-pack as product_id. */
  key: string;
  /** Display label (10, 20, 50, ...). Derived from dollar_amount. */
  dollars: number;
  /** Total credits awarded (credits_granted). */
  credits: number;
  /** Bonus percentage. Reads directly from bonus_pct on the product row. */
  bonusPct: number;
  /** Optional badge label — pulled from product name heuristically. */
  badge?: string;
}

/**
 * Convert a flat BillingProduct row into the Bundle shape the picker UI uses.
 */
function productToBundle(p: BillingProduct): Bundle {
  const dollars = Number(p.dollar_amount);
  const credits = p.credits_granted;
  const bonusPct = Number(p.bonus_pct);
  const nameLower = p.name.toLowerCase();
  let badge: string | undefined;
  if (nameLower.includes('popular')) badge = 'Most popular';
  else if (nameLower.includes('best value')) badge = 'Best value';
  return {
    key: p.id,
    dollars,
    credits,
    bonusPct: Number.isFinite(bonusPct) ? bonusPct : 0,
    ...(badge !== undefined ? { badge } : {}),
  };
}

export interface BuyCreditsModalProps {
  open: boolean;
  onClose: () => void;
  /** Optional callback fired after a successful purchase initiation. */
  onPurchaseInitiated?: (bundle: Bundle) => void;
  /** Whether the caller (admin/owner) should see the test-mode hint when
   *  the endpoint reports Stripe is not yet configured. Defaults false. */
  showTestCreditsHint?: boolean;
}

/**
 * Sub-agent QQQQQ (2026-07-31) — in-modal purchase state machine.
 *
 * Prior shape (Push 108 / Sub-agent OOOOO) hard-navigated to
 * /admin/workspace/usage?purchase=success on saved-card success — user
 * saw a blur → new page → PurchaseSuccessModal pop. Ean's ask: stay in
 * the modal through the entire flow.
 *
 * Phases:
 *   'pick'                 — bundle picker + Confirm footer (default)
 *   'stripe_coming_soon'   — STRIPE_ENABLED=false soft-fail
 *   'loading'              — POST /api/billing/purchase in flight; body
 *                            shows OlyticThinkingIndicator wave + copy
 *                            "Adding $X to your wallet…"; Cancel/close
 *                            disabled so the POST can't be orphaned
 *   'success'              — POST resolved ok:true; body shows compact
 *                            teal-check success shape (same visual DNA
 *                            as PurchaseSuccessModal); auto-dismiss 4s
 *                            OR Done button; olytic:wallet-changed
 *                            already fired so behind-modal balance
 *                            surfaces refresh
 *   'failure'              — POST failed (declined card, network, etc.);
 *                            muted iOS-style danger icon + Try again /
 *                            Use different card / Cancel buttons
 *
 * The hosted-checkout path (no saved card + click Confirm) still hard-
 * redirects to Stripe — nothing to render in-modal after the browser
 * leaves.
 */
type ScreenState =
  | { kind: 'pick' }
  | { kind: 'stripe_coming_soon'; bundle: Bundle }
  | { kind: 'loading'; bundle: Bundle }
  | { kind: 'success'; bundle: Bundle }
  | { kind: 'failure'; bundle: Bundle; message: string };

type BundlesState =
  | { kind: 'loading' }
  | { kind: 'loaded'; bundles: Bundle[] }
  | { kind: 'empty' }
  | { kind: 'error'; message: string };

/** Auto-dismiss delay for the in-modal success phase — matches the
 *  standalone PurchaseSuccessModal's default so both surfaces feel like
 *  the same product. */
const SUCCESS_AUTO_DISMISS_MS = 4000;

/** Per-tile state — which tile (if any) is mid-POST, and any tile-local error. */
interface TileBusyState {
  /** Key of the tile that's been clicked + is awaiting the POST response.
   *  null if no purchase is in flight. */
  submittingKey: string | null;
  /** Tile-local error message keyed by tile key. */
  tileErrors: Record<string, string>;
}

export function BuyCreditsModal({
  open,
  onClose,
  onPurchaseInitiated,
  showTestCreditsHint = false,
}: BuyCreditsModalProps) {
  const [screen, setScreen] = useState<ScreenState>({ kind: 'pick' });
  const [bundlesState, setBundlesState] = useState<BundlesState>({ kind: 'loading' });
  const [busy, setBusy] = useState<TileBusyState>({ submittingKey: null, tileErrors: {} });
  // Push #82.8 Phase 2 — selectedKey carries the bundle key the user has
  // clicked. Auto-defaults to the first bundle on load (Anthropic-style:
  // "$50" pre-selected so users without a strong preference get a sane
  // default). Null means no selection yet (still loading).
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // Push #85 — saved-card state. When card on file, Pay button uses the
  // one-click `purchaseSavedCard(dollars)` path instead of bouncing to
  // hosted Checkout. User can override with the "Use a different card"
  // link below the Pay button.
  const [savedCard, setSavedCard] = useState<SavedCardResponse | null>(null);
  const [forceHostedCheckout, setForceHostedCheckout] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Push 4-1an (2026-07-10) — pull the workspace's avg daily burn so we
  // can show "You currently spend ~X credits/day. {N} credits ≈ {days}
  // runway." context below the tile picker. Skipped when burn is unknown
  // (new / dormant workspace).
  const [avgDailyBurn, setAvgDailyBurn] = useState<number | null>(null);

  // Sub-agent QQQQQ (2026-07-31) — success-phase auto-dismiss timer ref.
  // Cleared on every phase change so stale phase state can never bleed
  // into the next open.
  const successDismissRef = useRef<number | null>(null);

  // Reset state every time the modal opens + fetch bundles + saved card.
  useEffect(() => {
    if (!open) return;
    setScreen({ kind: 'pick' });
    setBusy({ submittingKey: null, tileErrors: {} });
    setBundlesState({ kind: 'loading' });
    setSelectedKey(null);
    setSavedCard(null);
    setForceHostedCheckout(false);
    setAvgDailyBurn(null);
    if (successDismissRef.current !== null) {
      clearTimeout(successDismissRef.current);
      successDismissRef.current = null;
    }
    let cancelled = false;
    // Push 4-1an (2026-07-10) — best-effort wallet fetch for the runway
    // context caption. Reads account.avg_daily_burn_credits_14d.
    fetch('/api/me/wallet', { credentials: 'include' })
      .then(async (resp) => {
        if (!resp.ok) return;
        const data = (await resp.json()) as {
          account?: { avg_daily_burn_credits_14d?: number };
        };
        if (cancelled) return;
        const burn = data.account?.avg_daily_burn_credits_14d;
        setAvgDailyBurn(
          typeof burn === 'number' && Number.isFinite(burn) && burn > 0
            ? burn
            : null,
        );
      })
      .catch(() => {
        /* silent */
      });
    // Best-effort: fetch saved-card state. If the user isn't admin/owner
    // or there's no card on file, savedCard stays null and the Pay button
    // takes the hosted-Checkout path.
    fetchSavedCard()
      .then((card) => {
        if (cancelled) return;
        setSavedCard(card);
      })
      .catch(() => {
        // Silent — saved-card is non-blocking for the buy flow.
      });
    fetchBillingProducts()
      .then((res) => {
        if (cancelled) return;
        const creditPacks = res.products.filter((p) => p.kind === 'credit_pack');
        if (creditPacks.length === 0) {
          setBundlesState({ kind: 'empty' });
          return;
        }
        const bundles = creditPacks
          .slice()
          .sort((a, b) => Number(a.dollar_amount) - Number(b.dollar_amount))
          .map(productToBundle);
        setBundlesState({ kind: 'loaded', bundles });
        // Auto-select the smallest bundle (typically $10/$50) so the
        // footer CTA renders with a real amount on first paint. Users
        // can click a different tile to switch.
        if (bundles.length > 0) setSelectedKey(bundles[0]!.key);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof ApiError ? err.message : 'Could not load bundles.';
        setBundlesState({ kind: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Esc to dismiss (only when not mid-purchase — clicks during pending
  // could leave Stripe Checkout sessions orphaned).
  // Sub-agent QQQQQ (2026-07-31): also block Esc during the in-modal
  // 'loading' phase so the POST /api/billing/purchase can't be orphaned.
  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(e: KeyboardEvent) {
      if (
        e.key === 'Escape' &&
        busy.submittingKey === null &&
        screen.kind !== 'loading'
      ) {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, busy.submittingKey, screen.kind]);

  // Sub-agent QQQQQ (2026-07-31) — cleanup any pending auto-dismiss timer
  // when the modal unmounts (defensive; the open-effect already clears on
  // reopen, but a mid-success unmount without a reopen would leak).
  useEffect(() => {
    return () => {
      if (successDismissRef.current !== null) {
        clearTimeout(successDismissRef.current);
        successDismissRef.current = null;
      }
    };
  }, []);

  if (!open) return null;

  async function submitPurchase(bundle: Bundle) {
    // Push #85 — branch on saved-card state. When a card is on file +
    // the user hasn't overridden via "Use a different card", hit the
    // one-click /api/billing/purchase endpoint. Sub-agent QQQQQ
    // (2026-07-31) refactor: on success STAY IN THE MODAL — flip to
    // the 'success' phase instead of hard-navigating. On failure flip
    // to the 'failure' phase; user picks Try again / Different card /
    // Cancel from there. Hosted-checkout path (no saved card) keeps its
    // hard-redirect — the browser leaves Olytic so in-modal state is
    // irrelevant.
    const useSavedCard = savedCard?.has_card === true && !forceHostedCheckout;
    // Clear any prior tile-error for this key, set submitting. Only used
    // on the hosted-checkout branch below (in-modal branch uses `screen`).
    setBusy((prev) => ({
      submittingKey: bundle.key,
      tileErrors: { ...prev.tileErrors, [bundle.key]: '' },
    }));
    if (useSavedCard) {
      // Flip to in-modal 'loading' immediately. Ensures the Confirm
      // button can't be double-clicked (backdrop click + Esc are also
      // gated on screen.kind === 'loading').
      setScreen({ kind: 'loading', bundle });
    }
    try {
      if (useSavedCard) {
        const res = await purchaseSavedCard(bundle.dollars);
        // Note: purchase.ts returns 200 only on `ok: true` (charge
        // succeeded synchronously; webhook credits the wallet shortly).
        // The `ok: false, requires_action: true` case lands as an
        // ApiError(402, code='requires_action') in the catch below.
        if (res.ok === true) {
          onPurchaseInitiated?.(bundle);
          // Fire olytic:wallet-changed so any behind-modal balance
          // surfaces (CreditsPoolCard, ChatCreditPill, LowBalanceBanner)
          // re-fetch. Webhook credits the wallet shortly after; the SPA
          // event triggers the refetch that catches the new balance.
          try {
            window.dispatchEvent(new Event('olytic:wallet-changed'));
          } catch {
            /* ignore */
          }
          // Follow-up refetch nudge in 1.5s so the second wallet fetch
          // catches the webhook credit even if the first raced ahead of
          // Stripe's async billing__token_grant INSERT.
          window.setTimeout(() => {
            try {
              window.dispatchEvent(new Event('olytic:wallet-changed'));
            } catch {
              /* ignore */
            }
          }, 1500);
          // Flip to 'success' phase + arm the auto-dismiss timer.
          setScreen({ kind: 'success', bundle });
          setBusy({ submittingKey: null, tileErrors: {} });
          if (successDismissRef.current !== null) {
            clearTimeout(successDismissRef.current);
          }
          successDismissRef.current = window.setTimeout(() => {
            onClose();
          }, SUCCESS_AUTO_DISMISS_MS);
          return;
        }
        // Defensive belt: server unexpectedly returned 200 with
        // ok:false. Surface as an in-modal failure the user can retry.
        setBusy({ submittingKey: null, tileErrors: {} });
        setScreen({
          kind: 'failure',
          bundle,
          message: 'The charge did not complete. Try again or use a different card.',
        });
        return;
      }
      const res = await buyCheckoutTokenPack(bundle.key);
      onPurchaseInitiated?.(bundle);
      // Hard redirect to Stripe Checkout. The session URL is returned by
      // the endpoint when STRIPE_ENABLED=true.
      window.location.href = res.checkout_url;
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        // 503 = Stripe not configured. Flip the whole modal to the
        // "Payments coming soon" screen (not a tile-local error — this
        // is a global state).
        setBusy({ submittingKey: null, tileErrors: {} });
        setScreen({ kind: 'stripe_coming_soon', bundle });
        return;
      }
      // Sub-agent OOOOO (2026-07-31): 3DS challenge auto-fallback.
      // /api/billing/purchase returns 402 + code='requires_action' when
      // Stripe insists on Strong Customer Authentication. Full 3DS in
      // the modal would need Stripe Elements + confirmCardPayment;
      // simpler + more reliable: hand off to hosted Checkout with the
      // same bundle pre-selected, which runs Stripe's own 3DS flow and
      // then bounces the user back to /admin/workspace/usage?purchase=success.
      if (
        useSavedCard &&
        err instanceof ApiError &&
        err.status === 402 &&
        err.code === 'requires_action'
      ) {
        try {
          const hostedRes = await buyCheckoutTokenPack(bundle.key);
          onPurchaseInitiated?.(bundle);
          window.location.href = hostedRes.checkout_url;
          return;
        } catch (hostedErr) {
          const message =
            hostedErr instanceof ApiError
              ? hostedErr.message
              : 'Could not fall back to secure checkout.';
          setBusy({ submittingKey: null, tileErrors: {} });
          setScreen({ kind: 'failure', bundle, message });
          return;
        }
      }
      const message = err instanceof ApiError ? err.message : 'Could not start checkout.';
      if (useSavedCard) {
        // In-modal path — flip to 'failure' phase so the user gets
        // Try again / Different card / Cancel affordances without
        // being kicked back to the picker.
        setBusy({ submittingKey: null, tileErrors: {} });
        setScreen({ kind: 'failure', bundle, message });
        return;
      }
      // Hosted-checkout path — surface as a tile-local error so the
      // picker stays interactive.
      setBusy((prev) => ({
        submittingKey: null,
        tileErrors: { ...prev.tileErrors, [bundle.key]: message },
      }));
    }
  }

  function backdropClick(e: React.MouseEvent<HTMLDivElement>) {
    // No-op while mid-purchase — backdrop closes would orphan the Stripe call.
    // Sub-agent QQQQQ (2026-07-31) — also no-op during the in-modal
    // 'loading' phase (POST in flight); allow backdrop dismiss during
    // 'success' + 'failure' (transaction settled either way).
    if (busy.submittingKey !== null) return;
    if (screen.kind === 'loading') return;
    if (e.target === e.currentTarget) onClose();
  }

  const headerTitle =
    screen.kind === 'stripe_coming_soon'
      ? 'Almost ready'
      : screen.kind === 'success'
        ? 'Credits added'
        : screen.kind === 'failure'
          ? "Couldn't complete that purchase"
          : screen.kind === 'loading'
            ? 'Adding credits'
            : 'Buy credits';

  // Sub-agent QQQQQ (2026-07-31) — during 'loading', hide the close (×)
  // button chrome entirely so the user can't half-cancel mid-POST.
  const showCloseButton = screen.kind !== 'loading';
  // Sub-agent QQQQQ (2026-07-31) — success + failure phases render a
  // compact focused card without the wide bundle-picker chrome. Bump the
  // modal max-width down to match PurchaseSuccessModal's iOS-style
  // dimensions on those phases.
  const modalMaxWidth =
    screen.kind === 'success' || screen.kind === 'failure' || screen.kind === 'loading'
      ? 420
      : 640;

  return (
    <div className="modal-backdrop" onClick={backdropClick} role="dialog" aria-modal="true">
      <div ref={cardRef} className="modal-card" style={{ maxWidth: modalMaxWidth }}>
        <div className="modal-header">
          <h2 className="modal-title">{headerTitle}</h2>
          {showCloseButton && (
            <button
              type="button"
              className="modal-close"
              aria-label="Close"
              onClick={onClose}
              disabled={busy.submittingKey !== null}
            >
              ×
            </button>
          )}
        </div>

        <div className="modal-body">
          {screen.kind === 'pick' && (
            <BundlePicker
              state={bundlesState}
              selectedKey={selectedKey}
              busy={busy}
              avgDailyBurn={avgDailyBurn}
              onPick={(b) => setSelectedKey(b.key)}
              onReload={() => {
                setBundlesState({ kind: 'loading' });
                setSelectedKey(null);
                fetchBillingProducts()
                  .then((res) => {
                    const creditPacks = res.products.filter((p) => p.kind === 'credit_pack');
                    if (creditPacks.length === 0) {
                      setBundlesState({ kind: 'empty' });
                      return;
                    }
                    const bundles = creditPacks
                      .slice()
                      .sort((a, b) => Number(a.dollar_amount) - Number(b.dollar_amount))
                      .map(productToBundle);
                    setBundlesState({ kind: 'loaded', bundles });
                    if (bundles.length > 0) setSelectedKey(bundles[0]!.key);
                  })
                  .catch((err: unknown) => {
                    const message =
                      err instanceof ApiError ? err.message : 'Could not load bundles.';
                    setBundlesState({ kind: 'error', message });
                  });
              }}
            />
          )}

          {screen.kind === 'stripe_coming_soon' && (
            <StripeComingSoon
              bundle={screen.bundle}
              showTestCreditsHint={showTestCreditsHint}
              onBack={() => setScreen({ kind: 'pick' })}
              onClose={onClose}
            />
          )}

          {screen.kind === 'loading' && <LoadingPhase bundle={screen.bundle} />}

          {screen.kind === 'success' && (
            <SuccessPhase
              bundle={screen.bundle}
              onDone={onClose}
            />
          )}

          {screen.kind === 'failure' && (
            <FailurePhase
              bundle={screen.bundle}
              message={screen.message}
              hasSavedCard={savedCard?.has_card === true}
              onRetry={() => {
                // Return to picker with the same bundle selected so the
                // user can just click Confirm again.
                setSelectedKey(screen.bundle.key);
                setScreen({ kind: 'pick' });
              }}
              onTryDifferentCard={() => {
                // Hard-redirect to hosted Stripe Checkout with the
                // same bundle. Bypass submitPurchase entirely so we
                // don't need to worry about the setState-then-call
                // race (React batches, so forceHostedCheckout wouldn't
                // be true yet inside submitPurchase). Hosted Checkout
                // lets the user enter a new card + carries 3DS + saves
                // the new PM as the workspace default via
                // setup_future_usage=off_session.
                const bundle = screen.bundle;
                setForceHostedCheckout(true);
                setBusy({ submittingKey: bundle.key, tileErrors: {} });
                buyCheckoutTokenPack(bundle.key)
                  .then((res) => {
                    onPurchaseInitiated?.(bundle);
                    window.location.href = res.checkout_url;
                  })
                  .catch((err: unknown) => {
                    const message =
                      err instanceof ApiError
                        ? err.message
                        : 'Could not start secure checkout.';
                    setBusy({ submittingKey: null, tileErrors: {} });
                    setScreen({ kind: 'failure', bundle, message });
                  });
              }}
              onCancel={onClose}
            />
          )}
        </div>

        {/* Push #82.8 Phase 2. Anthropic-style footer: subtotal + total
           due summary on the left, Cancel + Pay $X now buttons on the
           right. Only renders on the pick screen with a loaded bundle
           selected. The Pay button is the dominant teal CTA. */}
        {screen.kind === 'pick' &&
          bundlesState.kind === 'loaded' &&
          selectedKey !== null &&
          (() => {
            const selected = bundlesState.bundles.find(
              (b) => b.key === selectedKey,
            );
            if (selected === undefined) return null;
            const isSubmitting = busy.submittingKey === selected.key;
            return (
              <div
                className="modal-footer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 16,
                  flexWrap: 'wrap',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    fontSize: 13,
                  }}
                >
                  <span className="muted" style={{ fontSize: 11 }}>
                    Total due
                  </span>
                  <span
                    style={{
                      fontSize: 20,
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatDollars(selected.dollars)}
                  </span>
                  <span className="muted" style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    {/* Block 3 / Q3b token-icon sweep */}
                    <IconCoin size={10} />
                    {selected.credits.toLocaleString()} credits
                    {selected.bonusPct > 0
                      ? ` · +${selected.bonusPct}% bonus`
                      : ''}
                  </span>
                </div>
                {/* Push 4-1d-y (2026-07-06): Cancel was `.btn btn-link` next
                    to Pay `.btn btn-primary` — mismatched visual weight in
                    a modal footer. Swapped Cancel to `.btn btn-secondary`
                    (outlined teal) so both share the outline+fill family
                    + min-height. */}
                <div className="button-row">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => void submitPurchase(selected)}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <Spinner />
                        {savedCard?.has_card === true && !forceHostedCheckout
                          ? 'Charging…'
                          : 'Preparing checkout…'}
                      </span>
                    ) : savedCard?.has_card === true && !forceHostedCheckout ? (
                      // Saved-card path — "Confirm" reads as one-click,
                      // not "opening a new page." Amount stays in the
                      // button so the CTA is self-describing.
                      `Confirm ${formatDollars(selected.dollars)}`
                    ) : (
                      `Pay ${formatDollars(selected.dollars)} now`
                    )}
                  </button>
                </div>
              </div>
            );
          })()}
        {/* Push #85. card-on-file caption when a saved card exists.
            Shows the user which card they're about to charge + offers a
            link to fall back to hosted Checkout (e.g. to enter a 3DS card
            or use a one-time card the user doesn't want to save). */}
        {savedCard?.has_card === true && screen.kind === 'pick' && (
          <div
            className="muted"
            style={{
              fontSize: 11,
              textAlign: 'center',
              marginTop: 8,
              lineHeight: 1.55,
            }}
          >
            {forceHostedCheckout ? (
              <>
                Using a different card via secure checkout.{' '}
                <button
                  type="button"
                  onClick={() => setForceHostedCheckout(false)}
                  className="btn-link"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--olytic-teal)',
                    cursor: 'pointer',
                    padding: 0,
                    font: 'inherit',
                  }}
                >
                  Use saved card instead
                </button>
              </>
            ) : (
              <>
                Charging your {formatCardBrand(savedCard.brand)} ending ••
                {savedCard.last4}.{' '}
                <button
                  type="button"
                  onClick={() => setForceHostedCheckout(true)}
                  className="btn-link"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--olytic-teal)',
                    cursor: 'pointer',
                    padding: 0,
                    font: 'inherit',
                  }}
                >
                  Use a different card
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Step components — split out to keep the main component scannable.
// ---------------------------------------------------------------------

function BundlePicker({
  state,
  selectedKey,
  busy,
  avgDailyBurn,
  onPick,
  onReload,
}: {
  state: BundlesState;
  selectedKey: string | null;
  busy: TileBusyState;
  /** Push 4-1an (2026-07-10) — workspace's recent avg daily burn, in
   *  credits/day. Null when unknown (new workspace, no 14-day history)
   *  or 0-burn; the caption is skipped in that case. */
  avgDailyBurn: number | null;
  onPick: (b: Bundle) => void;
  onReload: () => void;
}) {
  if (state.kind === 'loading') {
    return (
      <div
        className="muted"
        style={{ fontSize: 13, padding: '24px 0', textAlign: 'center' }}
        aria-live="polite"
      >
        Loading bundles…
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div>
        <div className="callout callout-danger">
          <p className="callout-body" style={{ margin: 0 }}>
            {state.message}
          </p>
        </div>
        <div style={{ marginTop: 12, textAlign: 'right' }}>
          <button type="button" className="btn btn-secondary" onClick={onReload}>
            Try again
          </button>
        </div>
      </div>
    );
  }
  if (state.kind === 'empty') {
    return (
      <div className="muted" style={{ fontSize: 13, padding: '24px 0' }}>
        No bundles are available right now. Workspace admins can issue a
        credit adjustment from the{' '}
        <a
          href="/admin/workspace/usage"
          style={{ color: 'var(--olytic-teal)' }}
        >
          Usage page
        </a>
        .
      </div>
    );
  }
  const bundles = state.bundles;
  const anyTileBusy = busy.submittingKey !== null;
  // Push 4-1an (2026-07-10) — resolve the selected bundle so the runway
  // caption can project days-of-runway for the current pick. If nothing
  // is selected yet or bundles are empty, no caption fires.
  const selectedBundle =
    selectedKey !== null ? bundles.find((b) => b.key === selectedKey) : undefined;
  return (
    <div>
      <p className="muted" style={{ fontSize: 13, lineHeight: 1.55, margin: '0 0 16px' }}>
        Choose an amount to start. You can always buy more later. Bigger
        bundles get more credits per dollar.
      </p>
      {/* Push 4-1an (2026-07-10) — runway context caption. Shows the
         workspace's recent burn rate + projected runway for the
         currently-selected bundle. Hidden when burn is unknown (new /
         dormant workspace, no 14-day history). */}
      {avgDailyBurn !== null && (
        <div
          className="muted"
          style={{
            fontSize: 12,
            lineHeight: 1.5,
            margin: '0 0 12px',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          Your workspace spends ~{Math.max(1, Math.round(avgDailyBurn))} credits/day.
          {selectedBundle !== undefined && (
            <>
              {' '}
              {selectedBundle.credits.toLocaleString()} credits ≈{' '}
              {Math.max(1, Math.floor(selectedBundle.credits / avgDailyBurn))} days of runway.
            </>
          )}
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: 10,
        }}
      >
        {bundles.map((b) => {
          const isSelected = selectedKey === b.key;
          const isThisTileBusy = busy.submittingKey === b.key;
          // Push #82.8 Phase 2 — only disable other tiles when a
          // checkout is mid-flight (preserves backdrop-click protection).
          // Selection itself never disables tiles — users can re-click
          // freely to switch their pick.
          const isOtherTileBusy = anyTileBusy && !isThisTileBusy;
          const tileError = busy.tileErrors[b.key] ?? '';
          return (
            <BundleTile
              key={b.key}
              bundle={b}
              selected={isSelected}
              busy={isThisTileBusy}
              disabled={isOtherTileBusy}
              errorMessage={tileError}
              onClick={() => onPick(b)}
            />
          );
        })}
      </div>
    </div>
  );
}

function BundleTile({
  bundle,
  selected,
  busy,
  disabled,
  errorMessage,
  onClick,
}: {
  bundle: Bundle;
  selected: boolean;
  busy: boolean;
  disabled: boolean;
  errorMessage: string;
  onClick: () => void;
}) {
  // Push #82.8 Phase 2 — selected state replaces the prior busy-tile
  // chrome. Busy-state spinner moved to the footer Pay button so the
  // tiles never need an in-place state-flip. The visual hierarchy is:
  // selected (teal ring) > hover (teal hint) > default.
  const borderColor = selected
    ? 'var(--olytic-teal)'
    : 'var(--surface-border, #e5e7eb)';
  const background = selected
    ? 'rgba(20, 184, 166, 0.06)'
    : 'var(--surface-bg, #fff)';
  return (
    <div>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="card card-padded"
        aria-pressed={selected}
        aria-busy={busy}
        style={{
          width: '100%',
          textAlign: 'left',
          cursor: disabled ? 'default' : 'pointer',
          padding: 14,
          border: `${selected ? '2px' : '1px'} solid ${borderColor}`,
          background,
          borderRadius: 8,
          transition: 'border-color 0.15s, background 0.15s, opacity 0.15s',
          position: 'relative',
          opacity: disabled ? 0.45 : 1,
          // Compensate for the 2px border on selected so the tile doesn't
          // bump 1px to the side. Cheaper than outline+ring.
          margin: selected ? '-1px' : 0,
        }}
        onMouseEnter={(e) => {
          if (disabled || selected) return;
          e.currentTarget.style.borderColor = 'var(--olytic-teal)';
          e.currentTarget.style.background = 'rgba(20, 184, 166, 0.04)';
        }}
        onMouseLeave={(e) => {
          if (disabled || selected) return;
          e.currentTarget.style.borderColor = 'var(--surface-border, #e5e7eb)';
          e.currentTarget.style.background = 'var(--surface-bg, #fff)';
        }}
      >
        {bundle.badge !== undefined && (
          <span
            className="uppercase"
            style={{
              fontSize: 10,
              color: 'var(--olytic-teal)',
              letterSpacing: 0.5,
              fontWeight: 600,
              display: 'block',
              marginBottom: 4,
            }}
          >
            {bundle.badge}
          </span>
        )}
        <div
          style={{
            fontSize: 22,
            fontWeight: 600,
            marginBottom: 2,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          ${bundle.dollars}
        </div>
        <div style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {/* Block 3 / Q3b token-icon sweep */}
          <IconCoin size={11} />
          {bundle.credits.toLocaleString()} credits
        </div>
        {bundle.bonusPct > 0 && (
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            +{bundle.bonusPct}% bonus
          </div>
        )}
      </button>
      {errorMessage !== '' && (
        <div
          role="alert"
          style={{
            marginTop: 6,
            padding: '6px 8px',
            fontSize: 12,
            color: 'var(--olytic-red, #b42318)',
            background: 'rgba(180, 35, 24, 0.06)',
            border: '1px solid rgba(180, 35, 24, 0.20)',
            borderRadius: 6,
            lineHeight: 1.4,
          }}
        >
          {errorMessage}
        </div>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 14,
        height: 14,
        borderRadius: '50%',
        border: '2px solid currentColor',
        borderTopColor: 'transparent',
        display: 'inline-block',
        animation: 'spin 0.7s linear infinite',
      }}
    />
  );
}

function StripeComingSoon({
  bundle,
  showTestCreditsHint,
  onBack,
  onClose,
}: {
  bundle: Bundle;
  showTestCreditsHint: boolean;
  onBack: () => void;
  onClose: () => void;
}) {
  return (
    <div>
      <div className="card card-padded" style={{ background: 'rgba(0, 0, 0, 0.02)' }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
          Payments not yet enabled
        </div>
        <p className="muted" style={{ fontSize: 13, margin: 0, lineHeight: 1.55 }}>
          Live payment processing is being finalized. When it's on, this
          same screen buys the {bundle.credits.toLocaleString()}-credit
          bundle in one click.
        </p>
      </div>
      {showTestCreditsHint && (
        <p className="muted" style={{ fontSize: 12, lineHeight: 1.55, margin: '16px 0 0' }}>
          Need credits in the meantime? Workspace admins can issue a
          credit adjustment from the{' '}
          <a
            href="/admin/workspace/usage"
            style={{ color: 'var(--olytic-teal)' }}
          >
            Usage page
          </a>
          .
        </p>
      )}
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        <button type="button" className="btn btn-primary" onClick={onClose}>
          Got it
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Sub-agent QQQQQ (2026-07-31) — in-modal loading / success / failure
// phases. Same visual DNA as PurchaseSuccessModal so the flow reads as
// one product, not two. Live inside the modal-body so the modal chrome
// (header + backdrop) stays uniform across every phase.
// ---------------------------------------------------------------------

/**
 * LoadingPhase — mid-POST body. Renders the brand-native
 * OlyticThinkingIndicator wave (reused from ActivityTimeline /
 * MessageItem so we ship consistent chat + purchase spinners) at a
 * slightly enlarged inline scale + a short, human copy line.
 * Deliberately no cancel affordance: the POST /api/billing/purchase is
 * in flight; letting the user half-cancel orphans the charge.
 */
function LoadingPhase({ bundle }: { bundle: Bundle }) {
  return (
    <div
      aria-live="polite"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        padding: '28px 8px 12px',
        textAlign: 'center',
      }}
    >
      <OlyticThinkingIndicator
        variant="inline"
        style={{ transform: 'scale(1.6)', transformOrigin: 'center' }}
      />
      <div style={{ fontSize: 14, color: 'var(--olytic-obsidian)', fontWeight: 500 }}>
        Adding {formatDollars(bundle.dollars)} to your wallet…
      </div>
      <div className="muted" style={{ fontSize: 12, lineHeight: 1.5 }}>
        Charging your card securely.
      </div>
    </div>
  );
}

/**
 * SuccessPhase — POST resolved ok:true. Compact iOS-style teal-check
 * confirmation card, matches PurchaseSuccessModal shape so users who
 * see the standalone version (first-time hosted-Checkout return) and
 * the in-modal version (subsequent one-click confirms) get the same
 * visual language. Renders inside the modal-body — the modal owns the
 * backdrop + header + auto-dismiss timer.
 */
function SuccessPhase({
  bundle,
  onDone,
}: {
  bundle: Bundle;
  onDone: () => void;
}) {
  const [checkmarkVisible, setCheckmarkVisible] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setCheckmarkVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        padding: '4px 8px 12px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          background: 'rgba(20, 184, 166, 0.12)',
          color: 'var(--olytic-teal)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 10,
          transform: checkmarkVisible ? 'scale(1)' : 'scale(0)',
          transition: 'transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
        aria-hidden="true"
      >
        <IconCheck size={22} />
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--olytic-obsidian)' }}>
        {formatDollars(bundle.dollars)} added
      </div>
      <div
        className="muted"
        style={{
          fontSize: 13,
          lineHeight: 1.5,
          margin: '2px 0 16px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          justifyContent: 'center',
        }}
      >
        <IconCoin size={13} />
        <span
          style={{
            color: 'var(--olytic-teal)',
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {bundle.credits.toLocaleString()}
        </span>{' '}
        credits ready to use.
      </div>
      <button
        type="button"
        className="btn btn-primary"
        onClick={onDone}
        autoFocus
        style={{ minWidth: 96 }}
      >
        Done
      </button>
    </div>
  );
}

/**
 * FailurePhase — POST failed. Muted iOS-style danger icon (amber tint,
 * not red-alarm) + human error copy + three recovery paths:
 *   1. Try again — return to picker with same bundle selected. User
 *      hits Confirm again; sometimes the transient card decline resolves.
 *   2. Use a different card — hard-redirect to hosted Stripe Checkout
 *      so the user can enter a new card. New card is saved as the
 *      workspace default via setup_future_usage=off_session (see
 *      token-pack.ts).
 *   3. Cancel — close the modal; user is back where they were.
 */
function FailurePhase({
  bundle,
  message,
  hasSavedCard,
  onRetry,
  onTryDifferentCard,
  onCancel,
}: {
  bundle: Bundle;
  message: string;
  hasSavedCard: boolean;
  onRetry: () => void;
  onTryDifferentCard: () => void;
  onCancel: () => void;
}) {
  void bundle;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        padding: '4px 8px 12px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          background: 'rgba(217, 119, 6, 0.10)',
          color: 'var(--olytic-amber-900, #92400e)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 10,
        }}
        aria-hidden="true"
      >
        <IconAlert size={22} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--olytic-obsidian)' }}>
        Charge did not go through
      </div>
      <p
        className="muted"
        style={{
          fontSize: 13,
          lineHeight: 1.55,
          margin: '4px 0 16px',
          maxWidth: 320,
        }}
      >
        {message}
      </p>
      <div
        className="button-row"
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}
      >
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        {hasSavedCard && (
          <button type="button" className="btn btn-secondary" onClick={onTryDifferentCard}>
            Use a different card
          </button>
        )}
        <button type="button" className="btn btn-primary" onClick={onRetry}>
          Try again
        </button>
      </div>
    </div>
  );
}

// Re-export Bundle type + product converter so other surfaces can render the
// same set of tiles (e.g., a marketing pricing component in /pricing —
// would call fetchBillingProducts and use productToBundle directly).
export type { Bundle };
export { productToBundle };
